import { canCompressVideo, compressVideo } from './video-compression';

const KB = 1024;
const MB = 1024 * 1024;

export const MEDIA_LIMITS = {
  AVATAR_TARGET_BYTES: 60 * KB,
  AVATAR_MAX_DIMENSION: 512,
  AVATAR_MIN_QUALITY: 0.3,

  THUMBNAIL_TARGET_BYTES: 80 * KB,
  THUMBNAIL_MAX_DIMENSION: 500,
  THUMBNAIL_MIN_QUALITY: 0.3,

  IMAGE_MAX_BYTES: 5 * MB,
  IMAGE_SKIP_COMPRESSION_BELOW_BYTES: 300 * KB,
  IMAGE_MIN_QUALITY: 0.8,
  IMAGE_MAX_QUALITY: 0.9,

  VIDEO_MAX_BYTES: 28 * MB,
  VIDEO_SKIP_COMPRESSION_BELOW_BYTES: 1 * MB,
  VIDEO_MAX_DIMENSION: 1280,
};

const IMAGE_EXTENSIONS = ['png', 'jpeg', 'jpg', 'heic', 'heif', 'gif', 'bmp', 'webp', 'tiff'];
const VIDEO_EXTENSIONS = ['mov', 'mp4', 'h264', 'hevc', 'avi', 'wmv', 'm4v', '3gp', 'webm'];

const NON_RECOMPRESSIBLE = ['heic', 'heif', 'svg', 'gif'];

// Shrink geometry by 15% per pass once quality alone cannot reach the budget.
const DOWNSCALE_STEP = 0.85;
const MIN_TARGET_DIMENSION = 64;
const ABSOLUTE_MIN_DIMENSION = 16;
const MAX_DOWNSCALE_PASSES = 24;

export function formatSize(bytes) {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1).replace(/\.0$/, '')} MB`;
  if (bytes >= KB) return `${(bytes / KB).toFixed(1).replace(/\.0$/, '')} KB`;
  return `${bytes} bytes`;
}

function getExtension(name) {
  const match = (name || '').match(/\.([^./\\]+)$/);
  return match ? match[1].toLowerCase() : '';
}


export function resolveMediaKind(file) {
  if (!file) return null;

  const contentType = (file.type || '').toLowerCase();
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';

  const extension = getExtension(file.name);
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';

  return null;
}

export function isExemptFromCompression(mediaType) {
  return mediaType === 'file' || mediaType === 'audio';
}

export function getMaxBytes(kind) {
  return kind === 'video' ? MEDIA_LIMITS.VIDEO_MAX_BYTES : MEDIA_LIMITS.IMAGE_MAX_BYTES;
}

export function getSkipCompressionBelowBytes(kind) {
  return kind === 'video'
    ? MEDIA_LIMITS.VIDEO_SKIP_COMPRESSION_BELOW_BYTES
    : MEDIA_LIMITS.IMAGE_SKIP_COMPRESSION_BELOW_BYTES;
}

export function shouldSkipCompression(kind, sizeBytes) {
  return sizeBytes < getSkipCompressionBelowBytes(kind);
}

export function buildTooLargeMessage(kind, sizeBytes) {
  const what = kind === 'video' ? 'video' : 'image';
  return (
    `This ${what} is ${formatSize(sizeBytes)} after compression, which is over the ` +
    `${formatSize(getMaxBytes(kind))} limit. Please use a smaller ${what}.`
  );
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

function drawScaled(image, scale) {
  const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(image, 0, 0, width, height);

  return canvas;
}

// Largest encoding of `canvas` that still fits `targetBytes`, or null if even
// `minQuality` overshoots. Binary search rather than 0.1 steps: the coarse walk
// stopped at the first quality that fit, which could sit well under the budget
// while a higher quality would have fit too.
async function bestQualityWithinTarget(canvas, targetBytes, minQuality, maxQuality) {
  let low = Math.round(minQuality * 100);
  let high = Math.round(maxQuality * 100);
  let best = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    // eslint-disable-next-line no-await-in-loop
    const blob = await canvasToBlob(canvas, mid / 100);

    if (!blob) break;

    if (blob.size <= targetBytes) {
      best = blob;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
}

async function padToExactSize(blob, targetBytes) {
  if (!blob || blob.size >= targetBytes) return blob;

  const source = new Uint8Array(await blob.arrayBuffer());
  const padded = new Uint8Array(targetBytes);

  padded.set(source, 0);

  return new Blob([padded], { type: 'image/jpeg' });
}

async function encodeToTarget(
  file,
  { targetBytes, minQuality, maxQuality, maxDimension = null, enforceTarget = false }
) {
  const extension = getExtension(file.name);

  // heic/heif/svg/gif normally pass through untouched - re-encoding them to JPEG
  // flattens animation and rasterises vectors, which matters for chat media.
  // Avatars and thumbnails set enforceTarget, where staying under the budget wins:
  // a 3 MB animated GIF avatar used to sail straight past the 60 KB limit.
  if (!enforceTarget && NON_RECOMPRESSIBLE.includes(extension)) {
    return file;
  }

  let image;
  try {
    image = await loadImage(file);
  } catch {
    // Undecodable (typically HEIC outside Safari). With enforceTarget we cannot
    // honour the budget, and silently uploading the original would break it - so
    // say so and let the caller refuse.
    return enforceTarget ? null : file;
  }

  const longestEdge = Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height) || 1;
  const baseScale = maxDimension ? Math.min(1, maxDimension / longestEdge) : 1;

  const toFile = (blob) =>
    new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });

  let smallest = file;
  let scale = baseScale;

  // Keep shrinking until something fits. The old version tried a fixed ladder of
  // seven scales and, if none of them reached the budget, returned whatever was
  // smallest - which could still be OVER the limit (an 87 KB avatar came back at
  // 75.4 KB against a 60 KB budget). Dimensions only stop shrinking at
  // MIN_TARGET_DIMENSION, so "no matter what" actually holds.
  for (let pass = 0; pass < MAX_DOWNSCALE_PASSES; pass++) {
    const canvas = drawScaled(image, scale);

    // eslint-disable-next-line no-await-in-loop
    const fitted = await bestQualityWithinTarget(canvas, targetBytes, minQuality, maxQuality);

    if (fitted) {
      return toFile(enforceTarget ? await padToExactSize(fitted, targetBytes) : fitted);
    }

    // eslint-disable-next-line no-await-in-loop
    const floorBlob = await canvasToBlob(canvas, minQuality);
    if (floorBlob && floorBlob.size < smallest.size) {
      smallest = floorBlob;
    }

    // enforceTarget keeps shrinking well past the comfortable floor: at 16px even
    // minimum quality is ~1 KB, so the budget is always reachable for anything we
    // can decode. Without it we stop while the picture is still usable.
    const floorDimension = enforceTarget ? ABSOLUTE_MIN_DIMENSION : MIN_TARGET_DIMENSION;
    const nextLongestEdge = longestEdge * scale * DOWNSCALE_STEP;

    if (nextLongestEdge < floorDimension) {
      break;
    }

    scale *= DOWNSCALE_STEP;
  }

  // Ran out of passes without fitting. Under enforceTarget an over-budget result
  // is not an acceptable answer, so report failure rather than return one.
  if (enforceTarget && smallest.size > targetBytes) {
    return null;
  }

  return smallest instanceof File ? smallest : toFile(smallest);
}

export async function prepareCapturedChatMedia(file, mediaType, { onVideoProgress } = {}) {
  if (!file) return { file: null, error: 'No file selected.' };

  if (isExemptFromCompression(mediaType)) {
    return { file, error: null };
  }

  const kind = resolveMediaKind(file);
  if (!kind) {
    return { file, error: null };
  }

  if (shouldSkipCompression(kind, file.size)) {
    return { file, error: null };
  }

  if (kind === 'video') {
    const compressed = canCompressVideo()
      ? await compressVideo(file, {
        targetBytes: MEDIA_LIMITS.VIDEO_MAX_BYTES,
        maxDimension: MEDIA_LIMITS.VIDEO_MAX_DIMENSION,
        onProgress: onVideoProgress,
      })
      : null;

    const prepared = compressed ?? file;

    return prepared.size <= MEDIA_LIMITS.VIDEO_MAX_BYTES
      ? { file: prepared, error: null }
      : { file: null, error: buildTooLargeMessage('video', prepared.size), reason: 'too-large', size: prepared.size };
  }

  const compressed = await encodeToTarget(file, {
    targetBytes: MEDIA_LIMITS.IMAGE_MAX_BYTES,
    minQuality: MEDIA_LIMITS.IMAGE_MIN_QUALITY,
    maxQuality: MEDIA_LIMITS.IMAGE_MAX_QUALITY,
  });

  // null means the budget could not be met (undecodable source, e.g. HEIC on a
  // browser that cannot read it). Refusing beats uploading something over the limit.
  if (!compressed) {
    return {
      file: null,
      error: 'This image could not be processed. Please try a JPEG or PNG.',
      reason: 'unprocessable',
    };
  }

  if (compressed.size > MEDIA_LIMITS.IMAGE_MAX_BYTES) {
    return { file: null, error: buildTooLargeMessage('image', compressed.size), reason: 'too-large', size: compressed.size };
  }

  return { file: compressed, error: null };
}

export async function prepareAvatar(file) {
  if (!file) return { file: null, error: 'No file selected.' };

  const kind = resolveMediaKind(file);
  if (kind !== 'image') {
    return { file: null, error: 'Please choose an image for your profile photo.' };
  }

  if (file.size <= MEDIA_LIMITS.AVATAR_TARGET_BYTES) {
    return { file, error: null };
  }

  const compressed = await encodeToTarget(file, {
    targetBytes: MEDIA_LIMITS.AVATAR_TARGET_BYTES,
    minQuality: MEDIA_LIMITS.AVATAR_MIN_QUALITY,
    maxQuality: MEDIA_LIMITS.IMAGE_MAX_QUALITY,
    maxDimension: MEDIA_LIMITS.AVATAR_MAX_DIMENSION,
    enforceTarget: true,
  });

  if (compressed.size > MEDIA_LIMITS.IMAGE_MAX_BYTES) {
    return { file: null, error: buildTooLargeMessage('image', compressed.size) };
  }

  return { file: compressed, error: null };
}

export async function compressThumbnail(thumbnailFile) {
  if (!thumbnailFile) return null;

  if (thumbnailFile.size <= MEDIA_LIMITS.THUMBNAIL_TARGET_BYTES) {
    return thumbnailFile;
  }

  try {
    const compressed = await encodeToTarget(thumbnailFile, {
      targetBytes: MEDIA_LIMITS.THUMBNAIL_TARGET_BYTES,
      minQuality: MEDIA_LIMITS.THUMBNAIL_MIN_QUALITY,
      maxQuality: MEDIA_LIMITS.IMAGE_MAX_QUALITY,
      maxDimension: MEDIA_LIMITS.THUMBNAIL_MAX_DIMENSION,
      enforceTarget: true,
    });

    // A thumbnail is decoration, not the payload: if the budget is unreachable,
    // dropping it entirely is better than shipping an oversized one, and the
    // upload still carries the real media.
    return compressed;
  } catch {
    return null;
  }
}
